import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ApiError, api } from '~/lib/api-client';
import { saveProposalKeys, dayOneKeys } from '~/lib/query-keys';
import type { SaveProposalItem } from '~/hooks/queries/use-save-proposal';
import type { SaveListEdit } from '~/features/chat/save-proposal-edit-draft';

interface DecisionBody {
  sessionId: string;
  decision: 'approve' | 'reject';
  /** FRONT-2321: the user's corrected list — approve only. */
  edit?: SaveListEdit;
}

/**
 * `POST /api/voice/save-proposal/decision`'s body. `ok: false` means nothing
 * was decided (already decided elsewhere, or replaced by a newer proposal) —
 * a 200, not an error, so callers must read it before telling Eddy anything.
 */
export interface SaveProposalDecisionResponse {
  ok: boolean;
  reason?: string;
  /** FRONT-2321: true when what was approved is the user's edited list. */
  edited?: boolean;
  /** FRONT-2321: the list as stored after the decision. */
  items?: SaveProposalItem[];
}

export interface SaveProposalDecisionInput {
  sessionId: string;
  edit?: SaveListEdit;
}

function useSaveProposalDecision(decision: 'approve' | 'reject') {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ sessionId, edit }: SaveProposalDecisionInput) => {
      const body: DecisionBody = { sessionId, decision, ...(edit ? { edit } : {}) };
      return api.post<SaveProposalDecisionResponse>('/api/voice/save-proposal/decision', body);
    },
    onSuccess: (_data, { sessionId }) => {
      qc.invalidateQueries({ queryKey: saveProposalKeys.bySession(sessionId) });
      // FRONT-1014 (Marko, 2026-07-29): the voice proposal already EXISTED
      // (the call ended and was processed) before either button could even
      // render — `isInterviewRecapDecided`'s voice branch is existence-based,
      // not decision-based. The day-one chat's "Enter Eddy" gate reads that
      // signal off `dayOneKeys`; this just keeps the cache from sitting on
      // its 30s staleTime instead of catching up promptly either way.
      qc.invalidateQueries({ queryKey: dayOneKeys.all });
    },
    onError: (err, { edit }) => {
      // FRONT-2321: a refused edit is a sentence for the person, shown in
      // the edit form itself — a toast would say it twice.
      if (edit && err instanceof ApiError && err.status === 400) return;
      const verb = decision === 'approve' ? 'Approve' : 'Reject';
      toast.error(`${verb} failed: ${(err as Error).message}`);
    },
  });
}

export function useApproveSaveProposal() {
  return useSaveProposalDecision('approve');
}

export function useRejectSaveProposal() {
  return useSaveProposalDecision('reject');
}
